import { useState } from 'react';
import { ChevronDown as ExpandToggleIcon } from 'lucide-react';
import Highlighter from 'react-highlight-words';
import { CustomerVisit } from 'types/customer';
import { Survey } from 'types/survey';
import { TranslationsKeys, useTranslate } from 'hooks/useTranslate';
import { Button } from 'components/ui/Button';
import { Danger } from 'components/ui/CustomIcons/Danger';
import { dateIsAfter, DateTimeFormat, formatDateTime } from 'utils/date';
import { classNameList } from 'utils/property';
import { getHighlighterProps } from '../util';
import styles from '../MySurveys.module.scss';

interface IProps {
  visit: CustomerVisit;
  surveyDefinition?: Survey;
  visitQuery?: string;
  buttonLabel: string;
  buttonClass: 'primary' | 'secondary';
  onSelect: () => void;
}

export const Visit = ({ visit, surveyDefinition, visitQuery, buttonLabel, buttonClass, onSelect }: IProps) => {
  const { t } = useTranslate();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const fullAddress = [visit.customer.address, visit.customer.city, visit.customer.state, visit.customer.country]
    .filter(Boolean).join(', ');
  const allParticipants = [visit.plannedVisit.owner.name, ...(visit.plannedVisit.attendees.map(a => a.name))]
    .filter(Boolean).join(', ');
  
  return (
    <div key={visit.plannedVisit.visitId} className={styles.mySurveysVisitsListPlannedVisitsCustomer}>
      <div className={styles.mySurveysVisitsListPlannedVisitsCustomerName}>
        <div>
          <span className={styles.mySurveysVisitsListPlannedVisitsCustomerNameTitle}><Highlighter {...getHighlighterProps(visit.customer.companyName, visitQuery)} /></span>
          {surveyDefinition && <><br/><Highlighter {...getHighlighterProps(surveyDefinition.name, visitQuery)} /></>}
        </div>
        <div>
          <Button
            variant={buttonClass}
            size="small"
            aria-label={t(TranslationsKeys.surveyIntroPage_noCustomerConfirmationAddCustomerButtonText)}
            onClick={onSelect}>
            {buttonLabel}
          </Button>
        </div>
      </div>
      <div className={styles.mySurveysVisitsListPlannedVisitsCustomerInfo}>
        {visit.plannedVisit.startDate !== '2000-01-01' && <div className={styles.mySurveysVisitsListPlannedVisitsCustomerInfoRow}>
          <div>{t(TranslationsKeys.common_plannedVisit)}:</div>
          <div>
            <Highlighter
              {...getHighlighterProps(
                formatDateTime(visit.plannedVisit.startDate, DateTimeFormat.DISPLAY),
                visitQuery
              )}
            />
            {dateIsAfter(new Date(), new Date(visit.plannedVisit.endDate)) &&
                <span className={styles.mySurveysOverdue}><Danger /> {t(TranslationsKeys.mySurveysPage_overdue)}</span>}
          </div>
        </div>}
        {fullAddress && <div className={styles.mySurveysVisitsListPlannedVisitsCustomerInfoRow}>
          <div>{t(TranslationsKeys.common_address)}:</div>
          <div>
            <Highlighter {...getHighlighterProps(fullAddress, visitQuery)} />
          </div>
        </div>}
        <div
          className={classNameList(
            styles.mySurveysVisitsListPlannedVisitsCustomerInfoCollapsible,
            !isExpanded &&
            !visitQuery &&
            styles.mySurveysVisitsListPlannedVisitsCustomerInfoCollapsibleCollapsed
          )}>
          {visit.customer.contactName && <div className={styles.mySurveysVisitsListPlannedVisitsCustomerInfoRow}>
              <div>{t(TranslationsKeys.common_contact)}:</div>
              <div>
                  <Highlighter {...getHighlighterProps(visit.customer.contactName, visitQuery)} />
              </div>
          </div>}
          {visit.customer.sector && <div className={styles.mySurveysVisitsListPlannedVisitsCustomerInfoRow}>
            <div>{t(TranslationsKeys.common_sector)}:</div>
            <div>
              <Highlighter {...getHighlighterProps(visit.customer.sector, visitQuery)} />
            </div>
          </div>}
          {visit.customer.businessAreaCode && <div className={styles.mySurveysVisitsListPlannedVisitsCustomerInfoRow}>
            <div>{t(TranslationsKeys.common_business)}:</div>
            <div>
              <Highlighter {...getHighlighterProps(visit.customer.businessAreaCode ?? '', visitQuery)} />
            </div>
          </div>}
          {visit.customer.customerId && <div className={styles.mySurveysVisitsListPlannedVisitsCustomerInfoRow}>
            <div>{t(TranslationsKeys.common_customerNumberAbbreviated)}:</div>
            <div>
              <Highlighter {...getHighlighterProps(visit.customer.customerId, visitQuery)} />
            </div>
          </div>}
          {visit.plannedVisit.visitTypeCode && <div className={styles.mySurveysVisitsListPlannedVisitsCustomerInfoRow}>
              <div>{t(TranslationsKeys.surveyIntroPage_customerVisit_visitType)}:</div>
              <div>
                  <Highlighter {...getHighlighterProps(visit.plannedVisit.visitTypeCode, visitQuery)} />
              </div>
          </div>}
          {allParticipants && <div className={styles.mySurveysVisitsListPlannedVisitsCustomerInfoRow}>
              <div>{t(TranslationsKeys.surveyIntroPage_customerVisit_participants)}:</div>
              <div>
                  <Highlighter {...getHighlighterProps(allParticipants, visitQuery)} />
              </div>
          </div>}
        </div>
        <div
          className={classNameList(
            styles.mySurveysVisitsListPlannedVisitsCustomerInfoExpandToggle,
            visitQuery && styles.mySurveysVisitsListPlannedVisitsCustomerInfoExpandToggleDisabled
          )}>
          <button onClick={() => setIsExpanded(!isExpanded)}>
            {!isExpanded && !visitQuery
              ? t(TranslationsKeys.common_showMore)
              : t(TranslationsKeys.common_showLess)}
            <ExpandToggleIcon
              className={classNameList(
                styles.mySurveysVisitsListPlannedVisitsCustomerInfoExpandToggleIcon,
                !isExpanded &&
                !visitQuery &&
                styles.mySurveysVisitsListPlannedVisitsCustomerInfoExpandToggleIconCollapsed
              )}
            />
          </button>
        </div>
      </div>
    </div>
  );
}